import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoAwesomeRounded as AutoAwesomeIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import LogFormShell from '../UI/LogFormShell';
import { getAdaptiveCareLensKey } from '../Dashboard/shared/adaptiveQuickLogConfig';
import AskRecordPanel from './AskRecordPanel';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
  ASK_COLORS,
  formatLogCount,
} from './askRecordUi';

const AskRecordTitleIcon = ({ accentInk, accentPrimary }) => (
  <Box
    sx={{
      width: { xs: 44, sm: 48 },
      height: { xs: 44, sm: 48 },
      borderRadius: '16px',
      display: 'grid',
      placeItems: 'center',
      bgcolor: accentPrimary,
      color: '#FFFFFF',
      boxShadow: `0 12px 24px ${alpha(accentInk, 0.18)}`,
    }}
  >
    <AutoAwesomeIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
  </Box>
);

const AskRecordSheet = ({
  accent,
  child,
  firstName,
  hasResult,
  logCount = 0,
  onClose,
  onOpenSource,
  onResultStateChange,
  open,
  sessionKey,
}) => {
  const isHospiceLens = getAdaptiveCareLensKey(child) === 'hospice';
  const inputPlaceholder = isHospiceLens
    ? 'Search comfort, meds, rest...'
    : 'Search sleep, meds, behavior...';
  const activeAccent = accent || {
    primary: ASK_ACCENT,
    ink: ASK_ACCENT_DARK,
    wash: ASK_COLORS.surface,
  };
  const accentPrimary = activeAccent.primary || ASK_ACCENT;
  const accentInk = activeAccent.ink || ASK_ACCENT_DARK;
  const accentWash = activeAccent.wash || ASK_COLORS.surface;

  return (
  <LogFormShell
    open={open}
    onClose={onClose}
    forceDrawer
    compactTitle
    titleLeadingContent={<AskRecordTitleIcon accentPrimary={accentPrimary} accentInk={accentInk} />}
    title={`Ask about ${firstName}'s care`}
    subtitle={`${formatLogCount(logCount)} · saved care notes`}
    mobileBreakpoint="md"
    maxWidth="sm"
    surfaceSx={{
      bgcolor: alpha(accentWash, 0.42),
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      boxShadow: `0 -18px 48px ${colors.landing.shadowPanel}`,
      borderTop: `1px solid ${colors.landing.borderLight}`,
      height: 'auto',
      maxHeight: '92dvh',
    }}
    bodySx={{
      px: { xs: 2, sm: 2.5 },
      pt: { xs: 1.1, sm: 1.35 },
      pb: hasResult ? 1.05 : 1.25,
    }}
    titleSx={{
      color: colors.semantic.ink,
      fontWeight: 800,
      letterSpacing: 0,
      fontSize: { xs: '1.28rem', sm: '1.46rem' },
      lineHeight: 1.08,
    }}
    subtitleSx={{
      color: ASK_COLORS.inkMuted,
      fontWeight: 650,
      fontSize: { xs: '0.82rem', sm: '0.9rem' },
    }}
    closeButtonSx={{
      bgcolor: alpha(colors.semantic.ink, 0.06),
      borderColor: 'transparent',
      '&:hover': { bgcolor: alpha(colors.semantic.ink, 0.1) },
    }}
    closeIconSx={{ color: alpha(colors.semantic.ink, 0.54) }}
  >
    <AskRecordPanel
      key={sessionKey}
      child={child}
      accent={activeAccent}
      compact={false}
      showDecorations={false}
      showHeader={false}
      resultLayout="conversation"
      inputPlaceholder={inputPlaceholder}
      formSx={{
        minHeight: { xs: 56, sm: 60 },
        borderRadius: 0,
        border: 0,
        bgcolor: 'transparent',
        boxShadow: 'none',
        px: { xs: 0.15, sm: 0.2 },
        py: 0.3,
        gridTemplateColumns: { xs: '28px minmax(0, 1fr) 44px', sm: '30px minmax(0, 1fr) 50px' },
        '&:focus-within': {
          boxShadow: 'none',
        },
      }}
      inputSx={{
        fontSize: { xs: '0.92rem', sm: '0.98rem' },
        fontWeight: 600,
      }}
      onOpenSource={onOpenSource}
      onResultStateChange={onResultStateChange}
      sx={{
        p: 0,
        bgcolor: 'transparent',
        background: 'transparent',
        border: 0,
        boxShadow: 'none',
        borderRadius: 0,
        overflow: 'visible',
      }}
    />
  </LogFormShell>
  );
};

export default AskRecordSheet;
